import { AssetGroup } from '@play-co/timestep-core';
import {
  ButtonView,
  ImageScaleView,
  View,
  animate,
} from '@play-co/timestep-core/ui';
import { user } from 'src/StateObserver';
import { downscale } from 'src/conf/scale';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { getLocale } from 'src/replicant/getters/user';
import { ShinyReward } from '../components/ShinyReward';
import { PetEntity } from 'src/game/logic/EntityManager';
import { createUIPet } from 'src/game/pet/pet.utils';
import ConfettiOverlay from 'src/game/minigame/match2-timestep/components/ConfettiOverlay';
import { animDuration, waitForItPromise } from 'src/lib/utils';
import { rescueLevelGetActive } from 'src/replicant/rescueLevel/rescueLevel.getters';

type CloseHandler = () => void;

export default class PopupRescueLevelWin extends PopupBasic {
  private header: ImageScaleView;
  private pet: PetEntity;
  private reward: ShinyReward;
  private tapToContinue: ImageScaleView;
  private confetti: ConfettiOverlay;
  private tapArea: ButtonView;
  private petFrom = { x: 0, y: 0 };
  private petTo = { x: 0, y: 0 };

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);
    super({
      superview: opts.superview,
      skipTitle: true,
      skipBackground: true,
      width,
      height,
      darkerBg: true,
      close: () => opts.close(),
      tracking: {
        feature: Feature.RESCUE_LEVEL._,
        $subFeature: Feature.RESCUE_LEVEL.POPUP_WIN,
      },
      zIndex: 10000,
    });

    this.buttonClose.hide();

    this.bg.updateOpts({
      backgroundColor: 'rgba(32,33,31,0.8)',
    });

    this.confetti = new ConfettiOverlay({
      superview: this.box,
    });

    this.header = new ImageScaleView({
      superview: this.box,
      image: 'assets/minigame/rescueLevel/celebration.png',
      scaleMethod: 'contain',
      width: downscale(1062),
      height: downscale(577),
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: this.box.style.height / 2 - 450,
    });

    const locale = getLocale(user());

    // Title
    new ImageScaleView({
      superview: this.header,
      image: `assets/minigame/rescueLevel/thank_you_${locale}.png`,
      scaleMethod: 'contain',
      width: downscale(658),
      height: downscale(115),
      centerOnOrigin: true,
      x: this.header.style.width / 2,
      y: this.header.style.height / 2 + 75,
    });

    this.reward = new ShinyReward({
      superview: this.box,
      x: this.box.style.width / 2,
      y: this.box.style.height / 2 + 300,
    });

    this.tapToContinue = new ImageScaleView({
      superview: this.box,
      image: `assets/minigame/rescueLevel/tap_to_continue_${locale}.png`,
      width: downscale(locale === 'en' ? 569 : 433),
      height: downscale(locale === 'en' ? 87 : 84),
      scaleMethod: 'contain',
      x: this.box.style.width / 2,
      y: this.box.style.height / 2 + 500,
      centerOnOrigin: true,
    });

    this.pet = createUIPet('happy');
    this.pet.setStatusAnimation('happy');
    this.pet.updateOpts({
      superview: this.box,
      x: this.box.style.width / 2,
      y: this.box.style.height / 2 + 50,
      scale: 0.8,
    });

    this.tapArea = new ButtonView({
      superview: this.box,
      width: width,
      height: height,
      zIndex: 1,
      onClick: () => {
        sounds.sfx.play('ok');
        opts.close();
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/minigame/rescueLevel/']),
    ]);
  }

  public async init(args: {
    retry?: boolean;
    petFromX?: number;
    petFromY?: number;
  }) {
    super.init({});

    // Update the reward
    const active = rescueLevelGetActive(user());
    if (active) {
      this.reward.setReward(active.reward);
    }

    // Hide all elements - they will be shown with animation
    this.header.hide();
    this.reward.hide();
    this.tapToContinue.hide();
    this.tapArea.hide();

    // Pet will be already on screen, starting from given position
    this.petFrom.x = args.petFromX ?? this.pet.style.x;
    this.petFrom.y = args.petFromY ?? this.pet.style.y - 300;
    this.petTo.x = this.pet.style.x;
    this.petTo.y = this.pet.style.y;

    this.pet.updateOpts(this.petFrom);
  }

  // This fadein animation should not scale up, just fade in
  public async fadeIn() {
    this.overlay.show();
    this.attachRoot();

    this.bg.updateOpts({ opacity: 0 });
    animate(this.bg)
      .clear()
      .then({ opacity: 1 }, animDuration, animate.easeOut);

    await Promise.all([
      // Pet can be shown straight away - it's loaded already
      this.showPetAnimated(),
      // Also ensure all other assets are loaded
      this.loadAssets(),
    ]);

    // All ready, hide overlay and show remaining elements
    this.overlay.hide();
    await this.showElementsAnimated();
  }

  private async showPetAnimated() {
    sounds.playSound('tada');
    await animate(this.pet)
      .now(this.petTo, 500, animate.easeOutBack)
      .toPromise();
  }

  private async showElementsAnimated() {
    sounds.playSound('applause');
    sounds.playSound('puzzle-win-confetti');

    // Start with the confetti
    this.confetti.show();

    // Present the header
    const headerY = this.header.style.y;
    this.header.updateOpts({ y: headerY - 500, visible: true, opacity: 0.5 });
    void animate(this.header)
      .now({ y: headerY, opacity: 1 }, 600, animate.easeOutBack)
      .toPromise();

    await waitForItPromise(300);

    // Show the reward
    sounds.playSound('gemsCollected');
    this.reward.updateOpts({ scale: 0, visible: true });
    void animate(this.reward)
      .now({ scale: 1 }, 300, animate.easeOutBack)
      .toPromise();

    await waitForItPromise(300);

    // Enable tap to continue now
    this.tapArea.show();

    // Show tap to continue
    const tapToContinueY = this.tapToContinue.style.y;
    this.tapToContinue.updateOpts({
      y: tapToContinueY + 300,
      visible: true,
      opacity: 0.5,
    });
    await animate(this.tapToContinue)
      .now({ y: tapToContinueY, opacity: 1 }, 600, animate.easeOutBack)
      .toPromise();
  }
}
